import { useReducer } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Customer } from './customer-search';
import { Vendor } from './vendor-search';
import { MarginStrategy, Priority, ProgramAttributesType } from './rule-submit';
import { useAlert } from '../../../hooks/useAlert';
import { useTranslate } from '@im/react-localization';

export class SingleRuleForm {
  autoDenialJustification?: string;
  autoDenialJustificationError?: string;
  businessJustification?: number;
  businessJustificationError?: string;
  effectiveDate: Dayjs = dayjs();
  effectiveDateError?: string;
  expirationDate: Dayjs = dayjs();
  expirationDateError?: string;
  marginStrategy?: MarginStrategy;
  marginStrategyError?: string;
  marginDelta?: number;
  marginDeltaError?: string;
  programAttributes?: ProgramAttributesType;
  requestPriority?: Priority;
  supressAutoDenial?: boolean;
  vendor?: Vendor;
  customer?: Customer;
  comment?: string;
}
export function useSingleRuleFrom(
  companyCode?: string,
  countryCode?: string,
  initialData?: SingleRuleForm
) {
  const translate = useTranslate();
  const showAlert = useAlert();
  const [formData, setFormData] = useReducer(
    (state: SingleRuleForm, next: Partial<SingleRuleForm>) => ({
      ...state,
      ...next,
    }),
    initialData ?? new SingleRuleForm()
  );

  return {
    formData,
    setFormData,
    validateFormData: (
      formData: SingleRuleForm
    ): formData is Required<SingleRuleForm> =>
      validateFormData(
        showAlert,
        companyCode,
        countryCode,
        formData,
        setFormData,
        translate
      ),
    validateMarginStrategy: (
      formData: Pick<SingleRuleForm, 'marginDelta' | 'marginStrategy'>
    ) => {
      validateMarginStrategy(formData, setFormData, translate);
    },
  };
}

// eslint-disable-next-line max-params
export function validateFormData(
  showAlert: any,
  companyCode: any,
  countryCode: any,
  formData: SingleRuleForm,
  setFormData: any,
  translate: any
) {
  if (!companyCode || !countryCode) {
    showAlert({
      text: translate('Pricing.MissingCountryOrCompanyCode'),
      type: 'error',
    });
    return false;
  }
  if (!formData.customer) {
    showAlert({
      text: translate('Pricing.ResellerNotSelected'),
      type: 'error',
    });
    return false;
  }
  if (!formData.vendor) {
    showAlert({
      text: translate('Pricing.VendorNotSelected'),
      type: 'error',
    });
    return false;
  }
  if (!formData.marginStrategy) {
    setFormData({
      marginStrategyError: translate('Pricing.RequiredError'),
    });
    return false;
  }
  if (!validateMarginStrategy(formData, setFormData, translate)) {
    return false;
  }
  if (!formData.businessJustification) {
    setFormData({
      businessJustificationError: translate('Pricing.RequiredError'),
    });
    return false;
  }
  if (formData.supressAutoDenial && !formData.autoDenialJustification) {
    setFormData({
      autoDenialJustificationError: translate('Pricing.RequiredError'),
    });
    return false;
  }
  return validateRuleFormDate(formData, setFormData, translate);
}

function validateMarginStrategy(
  formData: Pick<SingleRuleForm, 'marginDelta' | 'marginStrategy'>,
  setFormData: any,
  translate: any
) {
  if (!formData.marginDelta && formData.marginDelta !== 0) {
    setFormData({
      marginDeltaError: translate('Pricing.RequiredError'),
    });
    return false;
  }
  if (
    formData.marginStrategy === 'RETAIL_PRICE' &&
    (formData.marginDelta > 0 || formData.marginDelta <= -100)
  ) {
    setFormData({
      marginDeltaError: translate('Pricing.NegativeRetailMinusRequiredError'),
    });
    return false;
  }
  if (formData.marginStrategy === 'RETAIL_DELTA' && formData.marginDelta > 0) {
    setFormData({
      marginDeltaError: translate('Pricing.NegativeRetailDeltaRequiredError'),
    });
    return false;
  }
  if (
    formData.marginStrategy === 'REPLACEMENT_COST' &&
    (formData.marginDelta < -99.99 || formData.marginDelta > 99.99)
  ) {
    setFormData({
      marginDeltaError: translate('Pricing.PositiveCostPlusRequiredError'),
    });
    return false;
  }
  setFormData({
    marginDeltaError: undefined,
    marginStrategyError: undefined,
  });

  return true;
}

function validateRuleFormDate(
  formData: SingleRuleForm,
  setFormData: any,
  translate: any
) {
  if (!formData.effectiveDate?.isValid()) {
    setFormData({
      effectiveDateError: translate('Pricing.InvalidDateError'),
    });
    return false;
  }
  if (!formData.expirationDate?.isValid()) {
    setFormData({
      expirationDateError: translate('Pricing.InvalidDateError'),
    });
    return false;
  }
  if (formData.expirationDate.isBefore(formData.effectiveDate)) {
    setFormData({
      expirationDateError: translate(
        'Pricing.InvalidDateErrorMustBeAfterEffectiveDate'
      ),
    });
    return false;
  }
  return true;
}
